/**
 * Parent bubbles' titles, drawn on a canvas over the bubbles (the laptop's
 * DOM view; the phone's canvas draws its own, canvas-renderer.js).
 *
 * A title is curved over its bubble's rim, at a size in the map's units
 * (layout.js titleFontSize: 6% of the bubble's width, at least 14), so it
 * follows its bubble on screen at any zoom. The layout keeps room for it
 * (render.js titleBandFor, the same measure: canvas-text.js fitArcTitle):
 * titles do not run over each other, and none is left out for another.
 * It shows from MIN_TITLE_PX on screen and stops growing at MAX_TITLE_PX
 * (smaller than its room then): same-size bubbles show theirs at the same
 * zoom (Alex, 2026-10-16: "similar size bubbles should show or hide";
 * "you did find a nice mechanism where bubbles repel a bit. use that").
 * Leaves' names are HTML inside them (render.js, label-lod.js).
 *
 * Until 2026-10-15 a title was an SVG text in the map's units, 18 to 48:
 * seen whole, a large map's titles were a fraction of a pixel, and 20,000 of
 * them were built at every render. In between (2026-10-15/16) the size
 * followed the zoom, and titles that touched were left out: some bubbles
 * showed a title, others of the same size did not.
 *
 * Redrawn once a frame: with the layout's motion in the same frame
 * (drawNow), after a zoom, a pan, a render or a drag move at the next one
 * (schedule). A double click on a title edits it (mount's onEdit); the
 * mouse over a shortened title shows it whole.
 */
import * as LabelLOD from "./label-lod.js";
import { fitArcTitle, titleFont } from "./canvas-text.js";
import { titleFontSize, titleArcLength } from "./layout.js";

// A title shows from this size on screen (px), as leaves' names
// (label-lod.js), and grows no larger.
const MIN_TITLE_PX = LabelLOD.MIN_TITLE_SCREEN_PX;
const MAX_TITLE_PX = 32;

let host = null;
let canvas = null;
let ctx = null;
let getView = null;
let onEdit = null;
let frame = null;
// The titles of the last frame: {id, name, full, size, x, y, w, h, boxes}
// in canvas px (hit tests, checks).
let drawn = [];
// The bubbles displayed at the last frame, by id (titlePoint).
let shownNodes = new Map();
// A title's letters in the map's units, kept while its name and size do not
// change.
const layouts = new WeakMap();
// The canvas size and the colours, read when they change, not every frame:
// a size or a style read forces the browser to lay out the page first.
let width = 0;
let height = 0;
let colors = null;
let resizeObserver = null;
let themeObserver = null;

/**
 * Put the layer over the bubbles (after each full render of the view:
 * render() rebuilds #b-canvas's content).
 * @param {HTMLElement} hostEl - #b-canvas
 * @param {Function} viewFn - () => {zoom, panX, panY}
 * @param {{onEdit?: Function}} [options] - onEdit(id, clientX, clientY): a
 *   double click on a title
 */
export function mount(hostEl, viewFn, options = {}) {
  host = hostEl;
  getView = viewFn;
  onEdit = options.onEdit || null;
  canvas = document.createElement("canvas");
  canvas.className = "bubble-titles";
  canvas.setAttribute("aria-hidden", "true");
  hostEl.appendChild(canvas);
  ctx = canvas.getContext("2d");
  hostEl.addEventListener("dblclick", onDblClick, true);
  hostEl.addEventListener("mousemove", onMouseMove, { passive: true });
  width = hostEl.clientWidth;
  height = hostEl.clientHeight;
  colors = null;
  resizeObserver = new ResizeObserver(() => {
    if (!host) return;
    width = host.clientWidth;
    height = host.clientHeight;
    schedule();
  });
  resizeObserver.observe(hostEl);
  // A theme change (data-theme), the spotlight's strength (a style on the
  // root): colours read again.
  themeObserver = new MutationObserver(() => {
    colors = null;
    schedule();
  });
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "style", "class"] });
  schedule();
}

export function unmount() {
  if (frame) cancelAnimationFrame(frame);
  frame = null;
  host?.removeEventListener("dblclick", onDblClick, true);
  host?.removeEventListener("mousemove", onMouseMove);
  resizeObserver?.disconnect();
  themeObserver?.disconnect();
  resizeObserver = null;
  themeObserver = null;
  canvas?.remove();
  canvas = null;
  ctx = null;
  host = null;
  onEdit = null;
  drawn = [];
  shownNodes = new Map();
}

/** Draw again at the next frame (once, however many calls). */
export function schedule() {
  if (!canvas || frame) return;
  frame = requestAnimationFrame(draw);
}

/**
 * Draw now, in this frame: the layout's motion (physics.js) moves the
 * bubbles in an animation frame, and a title scheduled from there would be
 * drawn one frame late, behind its bubble.
 */
export function drawNow() {
  if (!canvas) return;
  if (frame) cancelAnimationFrame(frame);
  draw();
}

/** The titles drawn at the last frame (checks). */
export function getDrawn() {
  return drawn;
}

/** A title's size on screen (px) for a bubble of this width (map units), or 0 if not shown. */
export function titleSizeOnScreen(size, zoom) {
  const px = titleFontSize(size) * zoom;
  return px < MIN_TITLE_PX ? 0 : Math.min(px, MAX_TITLE_PX);
}

/**
 * Where to edit a parent's title from the keyboard: the middle of its drawn
 * title, else the top of its bubble if displayed (client coordinates).
 */
export function titlePoint(id) {
  if (!host) return null;
  const rect = host.getBoundingClientRect();
  const d = drawn.find((x) => x.id === id);
  if (d) return { x: rect.left + d.x + d.w / 2, y: rect.top + d.y + d.h / 2 };
  const n = shownNodes.get(id);
  const t = getView?.();
  if (!n || !t?.zoom) return null;
  const c = LabelLOD.worldCenter(n);
  return { x: rect.left + t.panX + c.x * t.zoom, y: rect.top + t.panY + (c.y - n.radius) * t.zoom };
}

/** The drawn title under a point (client coordinates), if any. */
function titleAt(clientX, clientY) {
  if (!drawn.length) return null;
  const rect = host.getBoundingClientRect();
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  for (const d of drawn) {
    if (x < d.x - 2 || x > d.x + d.w + 2 || y < d.y - 2 || y > d.y + d.h + 2) continue;
    for (const b of d.boxes) {
      if (x >= b.x - 2 && x <= b.x + b.w + 2 && y >= b.y - 2 && y <= b.y + b.h + 2) return d;
    }
  }
  return null;
}

function onDblClick(e) {
  const hit = titleAt(e.clientX, e.clientY);
  if (!hit) return;
  e.stopPropagation();
  e.preventDefault();
  onEdit?.(hit.id, e.clientX, e.clientY);
}

function onMouseMove(e) {
  if (!host || e.buttons) return;
  const hit = titleAt(e.clientX, e.clientY);
  const tip = hit && hit.name !== hit.full ? hit.full : "";
  if (host.title !== tip) host.title = tip;
}

/** A title's letters in the map's units: as the layout measured it. */
function worldLayout(n, name) {
  const fontSize = titleFontSize(2 * n.radius);
  const r = n.titleRadius ?? n.radius + 4;
  const key = `${name}\u0000${fontSize}\u0000${r}`;
  const cached = layouts.get(n);
  if (cached?.key === key) return cached;
  const layout = { key, fontSize, r, ...fitArcTitle(name, fontSize, titleArcLength(r)) };
  layouts.set(n, layout);
  return layout;
}

function drawTitle(n, name, t, dpr, textColor, alpha) {
  const layout = worldLayout(n, name);
  if (!layout.letters.length) return null;
  const size = Math.min(layout.fontSize * t.zoom, MAX_TITLE_PX);
  // Letters scale with the size (smaller than the room once at the largest).
  const k = size / layout.fontSize;
  const r = layout.r * t.zoom;
  const c = LabelLOD.worldCenter(n);
  const sx = t.panX + c.x * t.zoom;
  const sy = t.panY + c.y * t.zoom;
  // Nothing of it on screen: the title lies within r + size of the centre.
  if (sx + r + size < 0 || sy + r + size < 0 || sx - r - size > width || sy - r - size > height) return null;

  const total = layout.width * k;
  const angles = [];
  const boxes = [];
  const mid = r + size * 0.35;
  let s = -total / 2;
  for (const adv of layout.advances) {
    const w = adv * k;
    const a = (s + w / 2) / r;
    s += w;
    angles.push(a);
    const side = Math.max(w, size * 0.75);
    boxes.push({ x: sx + mid * Math.sin(a) - side / 2, y: sy - mid * Math.cos(a) - size / 2, w: side, h: size });
  }
  if (!boxes.some((b) => b.x + b.w > 0 && b.y + b.h > 0 && b.x < width && b.y < height)) return null;

  const done = n.status === "done";
  ctx.font = titleFont(size);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = textColor;
  ctx.globalAlpha = alpha * (done ? 0.7 : 1);
  for (let i = 0; i < angles.length; i++) {
    const cos = Math.cos(angles[i]);
    const sin = Math.sin(angles[i]);
    ctx.setTransform(dpr * cos, dpr * sin, -dpr * sin, dpr * cos, dpr * (sx + r * sin), dpr * (sy - r * cos));
    ctx.fillText(layout.letters[i], 0, 0);
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  if (done) {
    const half = total / 2 / r;
    ctx.strokeStyle = textColor;
    ctx.lineWidth = Math.max(1, size / 14);
    ctx.beginPath();
    ctx.arc(sx, sy, r + size * 0.3, -Math.PI / 2 - half, -Math.PI / 2 + half);
    ctx.stroke();
  }
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const b of boxes) {
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.w);
    y1 = Math.max(y1, b.y + b.h);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0, boxes, name: layout.text, size };
}

function draw() {
  frame = null;
  drawn = [];
  shownNodes = new Map();
  if (!canvas || !host?.isConnected) return;
  const dpr = window.devicePixelRatio || 1;
  if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);
  const t = getView?.();
  if (!t?.zoom) return;

  if (!colors) {
    const style = getComputedStyle(host);
    colors = {
      text: style.getPropertyValue("--text-primary").trim() || "#dddddd",
      dim: parseFloat(style.getPropertyValue("--tag-dim-amount")) || 0,
    };
  }
  // Tag spotlight: the titles of the bubbles it fades fade too (main.css).
  const filtering = host.classList.contains("tag-filtering");

  for (const n of LabelLOD.shownParents()) {
    shownNodes.set(n.id, n);
    const name = n.name ?? n.dataNode?.name ?? "";
    if (!name || titleFontSize(2 * n.radius) * t.zoom < MIN_TITLE_PX) continue;
    const alpha = filtering && n.spotlight === "dim" ? 1 - colors.dim * 0.9 : 1;
    const placed = drawTitle(n, name, t, dpr, colors.text, alpha);
    if (placed) drawn.push({ id: n.id, full: name, ...placed });
  }
  ctx.globalAlpha = 1;
}
